import { create } from "zustand";

export type GraphicsStatus = "initializing" | "normal" | "compatible" | "unavailable";
export type GraphicsProfile = "normal" | "webgl2" | "webgl1";

/** État commun aux composants métier et aux extensions du viewer. */
export const useGraphicsState = create<{
  status: GraphicsStatus;
  profile: GraphicsProfile | null;
}>(() => ({ status: "initializing", profile: null }));

export function isGraphicsError(error: unknown): boolean {
  const message = typeof error === "object" && error !== null && "message" in error
    ? String(error.message) : String(error);
  return /webgl|framebuffer|renderbuffer|shader|graphics context|context lost/i.test(message);
}

/** Chaque tentative repart d’un contexte propre ; aucune boucle de récupération implicite. */
export async function initializeGraphicsProfiles<T>(
  attempt: (profile: GraphicsProfile) => Promise<T>,
  cleanup: () => void,
  isCurrent: () => boolean,
): Promise<T> {
  useGraphicsState.setState({ status: "initializing", profile: null });
  for (const profile of ["normal", "webgl2", "webgl1"] as const) {
    try {
      const result = await attempt(profile);
      if (!isCurrent()) throw new Error("Initialisation annulée");
      useGraphicsState.setState({ status: profile === "normal" ? "normal" : "compatible", profile });
      return result;
    } catch (error) {
      // Une ancienne tentative ne doit jamais détruire le nouveau viewer.
      if (!isCurrent()) throw error;
      cleanup();
      if (!isGraphicsError(error) || profile === "webgl1") {
        useGraphicsState.setState({ status: "unavailable", profile: null });
        throw error;
      }
    }
  }
  throw new Error("Aucun profil graphique disponible");
}
